import { createFileRoute, Link } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { buildMeta } from "@/lib/seo";
import { breadcrumbScript } from "@/lib/breadcrumbs";
import { Plane } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => {
    const base = buildMeta({
      title: "O nas — kim jesteśmy i jak działamy | Tanie Latanie",
      description: "Dowiedz się, jak Tanie Latanie korzysta z Aviasales, skąd pochodzą ceny okazji i gdzie potwierdzić bagaż oraz warunki rezerwacji.",
      path: "/about",
    });
    return {
      ...base,
      scripts: [
        
        breadcrumbScript([
          { name: "Strona główna", path: "/" },
          { name: "O nas", path: "/about" },
        ]),
      ],
    };
  },
  component: About,
});

function About() {
  return (
    <div className="min-h-screen">
      <Header />
      <section className="container mx-auto max-w-3xl px-4 py-16">
        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-sunset shadow-glow">
          <Plane className="h-8 w-8 -rotate-45 text-primary-foreground" />
        </div>
        <h1 className="font-display text-5xl font-bold leading-tight md:text-6xl">
          Latamy <span className="text-gradient-sunset">tanio.</span><br />Codziennie.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground">
          Tanie Latanie to polska wyszukiwarka lotów stworzona z myślą o podróżnikach,
          którzy chcą porównywać połączenia i świadomie planować podróż. Korzystamy
          z wyszukiwarki Aviasales udostępnianej przez Travelpayouts. Oferty zależą od dat,
          lotnisk i liczby pasażerów.
        </p>

        <div className="prose prose-invert mt-10 max-w-none space-y-6 text-muted-foreground">
          <div className="glass rounded-lg p-6">
            <h2 className="!mt-0 font-display text-2xl font-bold text-foreground">Skąd pochodzą wyniki?</h2>
            <p className="mt-2">
              Wyniki wyszukiwania udostępnia Aviasales. Sekcja okazji korzysta z danych
              o cenach zapisanych na podstawie wcześniejszych wyszukiwań. Taka cena może
              się zmienić lub przestać być dostępna. Data przy ofercie oznacza termin wylotu,
              nie cenę za dzień podróży.
            </p>
          </div>

          <div className="glass rounded-lg p-6">
            <h2 className="!mt-0 font-display text-2xl font-bold text-foreground">Czy to faktycznie taniej?</h2>
            <p className="mt-2">
              Porównaj konkretne daty, lotniska i zakres usług. Cena zależy od wybranej
              taryfy: większa walizka kabinowa, bagaż rejestrowany, miejsce czy inne dodatki
              mogą być płatne osobno. Aktualną cenę końcową i warunki potwierdzasz
              u wybranego sprzedawcy przed płatnością. Nie gwarantujemy najniższej ceny.
            </p>
          </div>

          <div className="glass rounded-lg p-6">
            <h2 className="!mt-0 font-display text-2xl font-bold text-foreground">Dla kogo to zrobiliśmy?</h2>
            <p className="mt-2">
              Dla wszystkich, którzy traktują latanie jak narzędzie, a nie luksus. Dla
              studentów lecących na Erasmus, dla freelancerów pracujących z Lizbony przez
              tydzień, dla rodzin planujących wakacje pół roku do przodu i dla tych, którzy
              w piątek wieczorem postanawiają, że w sobotę rano lecą gdziekolwiek. Nasza
              sekcja <Link to="/gdziekolwiek" className="text-primary hover:underline">Gdziekolwiek</Link>{" "}
              i <Link to="/okazje" className="text-primary hover:underline">Okazje</Link> powstały
              właśnie z myślą o tej drugiej grupie — kiedy kierunek jest mniej ważny niż cena.
            </p>
          </div>

          <div className="glass rounded-lg p-6">
            <h2 className="!mt-0 font-display text-2xl font-bold text-foreground">Czego u nas nie znajdziesz</h2>
            <p className="mt-2">
              Nie sprzedajemy biletów bezpośrednio i nie obsługujemy płatności ani zmian
              rezerwacji. Zakup kończysz u przewoźnika lub agenta wybranego w wynikach.
              W sprawach dotyczących kupionego biletu kontaktuj się z jego sprzedawcą;
              potwierdzenie i warunki otrzymasz zgodnie z zasadami tego podmiotu.
            </p>
          </div>

          <div className="glass rounded-lg p-6">
            <h2 className="!mt-0 font-display text-2xl font-bold text-foreground">Jak porównywać oferty?</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Wybierz konkretne lotniska i daty; strona kraju służy do wyboru kierunku.</li>
              <li>Sprawdź, czy porównujesz lot w jedną stronę czy podróż z powrotem.</li>
              <li>Porównaj bagaż, godziny oraz koszt dojazdu do lotniska.</li>
              <li>Przeczytaj warunki taryfy, zmian i zwrotów u sprzedawcy.</li>
              <li>Praktyczne wskazówki znajdziesz w <Link to="/" className="text-primary hover:underline">poradniku Tanie Latanie</Link>.</li>
            </ul>
          </div>

          <div className="glass rounded-lg p-6">
            <h2 className="!mt-0 font-display text-2xl font-bold text-foreground">Kontakt</h2>
            <p className="mt-2">
              Masz pytanie? Napisz do nas przez <Link to="/contact" className="text-primary hover:underline">formularz kontaktowy</Link>.
            </p>
          </div>
        </div>

        <div className="mt-10">
          <Link to="/">
            <Button variant="hero" size="lg">Wyszukaj loty →</Button>
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
